'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { faThumbsUp, faThumbsDown, faChevronRight } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { Rating, Star } from '@smastrom/react-rating';
import '@smastrom/react-rating/style.css';
import addReviewAction from '@/app/_lib/actions/AddReviewAction';
import voteReviewHelpfulAction from '@/app/_lib/actions/VoteReviewHelpfulAction';
import FormError from '@/app/_components/FormError';
import RecaptchaDisclaimer from '@/app/_components/RecaptchaDisclaimer';
import { useGlobalMessage } from '@/app/_lib/hooks';
import { useReCaptcha } from 'next-recaptcha-v3';
import { useProductContext } from '../_context';

// Shared star styles
const starStyles = {
  itemShapes: Star,
  itemStrokeWidth: 0,
  activeFillColor: '#FCB600',
  inactiveFillColor: '#D1D5DB'
};

// Format review timestamp as relative time description. `now` is passed in
// (never read here): reading the clock during render marks the tree dynamic
// under cacheComponents, which knocks the ENTIRE product page body - schema,
// price, Add to Cart - out of the prerendered HTML for any product that has
// a review (the 2026-08 SEO incident). Only call this with a clock value
// obtained in an effect.
const formatReviewTime = (timestamp, now) => {
  if (!timestamp) return '';
  const reviewDate = new Date(timestamp * 1000); // Unix timestamp to JS Date
  const diffMs = now - reviewDate;
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  const diffMonths = diffMs / (1000 * 60 * 60 * 24 * 30);
  const diffYears = diffMs / (1000 * 60 * 60 * 24 * 365);
  if (diffYears >= 1) return 'Over a year ago';
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return 'Today';
  if (diffDays < 2) return 'Yesterday';
  if (diffDays <= 7) return 'This week';
  if (diffDays <= 28) return 'A few weeks ago';
  if (diffMonths <= 2) return 'About a month ago';
  if (diffMonths <= 6) return 'A few months ago';
  return 'Several months ago';
};

// Absolute, deterministic date label - safe to prerender. Pinned to en-US/UTC
// so the server HTML and the client hydration pass produce identical text.
const formatReviewDate = timestamp => {
  if (!timestamp) return '';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

// Server render / raw HTML: absolute date (also better for SEO than relative
// text). After hydration an effect swaps in the usual relative label.
const ReviewTime = t0 => {
  const $ = _c(5);
  const {
    timestamp
  } = t0;
  let t1;
  if ($[0] !== timestamp) {
    t1 = () => formatReviewDate(timestamp);
    $[0] = timestamp;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [label, setLabel] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== timestamp) {
    t2 = () => {
      setLabel(formatReviewTime(timestamp, Date.now()));
    };
    t3 = [timestamp];
    $[2] = timestamp;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  return label;
};
const Reviews = t0 => {
  const $ = _c(153);
  const {
    sub_product_id,
    sub_product_name,
    sub_product_thumb,
    review_stats,
    initialReviews: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initialReviews = t2;
  const router = useRouter();
  const addGlobalMessage = useGlobalMessage();
  const {
    executeRecaptcha
  } = useReCaptcha();
  const {
    addReviewFormOpen,
    setAddReviewFormOpen
  } = useProductContext();
  const reviews = initialReviews;
  const totalRating = review_stats?.average_rating || 0;
  const reviewCount = review_stats?.review_count || 0;
  const [isPending, setIsPending] = useState(false);
  const [newReviewScore, setNewReviewScore] = useState(0);
  const [reviewErrors, setReviewErrors] = useState(0);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [newReview, setNewReview] = useState(t3);
  let t4;
  if ($[3] !== initialReviews) {
    t4 = () => {
      const counts = {};
      for (const review of initialReviews) {
        if (review.helpful_counts) {
          counts[review.review_id] = review.helpful_counts;
        }
      }
      return counts;
    };
    $[3] = initialReviews;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [helpfulCounts, setHelpfulCounts] = useState(t4);
  const [votingReviewId, setVotingReviewId] = useState(null);
  let t5;
  if ($[5] !== addReviewFormOpen || $[6] !== setAddReviewFormOpen) {
    t5 = () => {
      setAddReviewFormOpen(!addReviewFormOpen);
    };
    $[5] = addReviewFormOpen;
    $[6] = setAddReviewFormOpen;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleAddReviewClick = t5;
  let t6;
  if ($[8] !== setAddReviewFormOpen) {
    t6 = () => {
      setAddReviewFormOpen(false);
    };
    $[8] = setAddReviewFormOpen;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const closeReviewAddForm = t6;
  let t7;
  if ($[10] !== addGlobalMessage || $[11] !== votingReviewId) {
    t7 = async (review_id, vote_type) => {
      if (votingReviewId) {
        return;
      }
      setVotingReviewId(review_id);
      const result = await voteReviewHelpfulAction({
        review_id,
        vote_type
      });
      if (result.success) {
        setHelpfulCounts(prev => ({
          ...prev,
          [review_id]: result.counts
        }));
        addGlobalMessage({
          message: result.message,
          messageType: "success"
        });
      } else {
        addGlobalMessage({
          message: result.message,
          messageType: result.requiresAuth ? "info" : "error"
        });
      }
      setVotingReviewId(null);
    };
    $[10] = addGlobalMessage;
    $[11] = votingReviewId;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleVote = t7;
  let t8;
  if ($[13] !== newReview) {
    t8 = event => {
      const updatedReview = {
        ...newReview
      };
      updatedReview[event.target.name] = event.target.value;
      setNewReview(updatedReview);
    };
    $[13] = newReview;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleInputChange = t8;
  let t9;
  if ($[15] !== addGlobalMessage || $[16] !== executeRecaptcha || $[17] !== newReviewScore || $[18] !== router || $[19] !== setAddReviewFormOpen || $[20] !== sub_product_id) {
    t9 = async formData => {
      setIsPending(true);
      setReviewErrors({});
      const recaptchaToken = await executeRecaptcha("add_review");
      if (!recaptchaToken) {
        setReviewErrors({
          general: "Security verification failed. Please try again."
        });
        setIsPending(false);
        return;
      }
      const requestData = {
        sub_product_id,
        user_alias: formData.get("user_alias"),
        email: formData.get("email"),
        review_title: formData.get("review_title"),
        review_score: newReviewScore,
        review: formData.get("review"),
        recaptchaToken
      };
      const addReviewResponse = await addReviewAction(requestData);
      setIsPending(false);
      if (addReviewResponse.success) {
        const message = {
          message: "Your review was added.  Once reviewed it will be published here.",
          messageType: "success"
        };
        addGlobalMessage(message);
        setAddReviewFormOpen(false);
        setNewReview({});
        setNewReviewScore(0);
        router.refresh();
      }
      if (addReviewResponse.errors) {
        setReviewErrors(addReviewResponse.errors);
      }
    };
    $[15] = addGlobalMessage;
    $[16] = executeRecaptcha;
    $[17] = newReviewScore;
    $[18] = router;
    $[19] = setAddReviewFormOpen;
    $[20] = sub_product_id;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleAddReviewSubmit = t9;
  const hasReviews = reviews && reviews.length > 0;
  const hasReviewErrors = Object.values(reviewErrors || {}).some(Boolean);
  const t10 = `pb-2 ${!hasReviews ? "bg-gray-3 rounded-lg p-6" : ""}`;
  const t11 = hasReviews ? "mb-8" : "mb-2";
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <h3 className="text-3xl font-extralight">Reviews</h3>;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== hasReviews || $[24] !== reviewCount || $[25] !== totalRating) {
    t13 = hasReviews && reviewCount > 0 && <div className="flex items-center"><Rating value={totalRating} readOnly={true} style={{
        maxWidth: 85
      }} itemStyles={starStyles} /><span className="text-sm text-light ml-2"><span className="font-medium">{totalRating}</span>/5 ({reviewCount} {reviewCount === 1 ? "review" : "reviews"})</span></div>;
    $[23] = hasReviews;
    $[24] = reviewCount;
    $[25] = totalRating;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13) {
    t14 = <div className="mb-4">{t12}{t13}</div>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="border-b border-gray-8" />;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t11 || $[31] !== t14) {
    t16 = <div className={t11}>{t14}{t15}</div>;
    $[30] = t11;
    $[31] = t14;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <FontAwesomeIcon icon={faPenToSquare} className="text-sm me-2 text-light group-hover:text-primary" />;
    t18 = <span className="text-sm underline">Write a Review</span>;
    $[33] = t17;
    $[34] = t18;
  } else {
    t17 = $[33];
    t18 = $[34];
  }
  const t19 = `text-xs ms-2 text-light transition-transform duration-300 ${addReviewFormOpen ? "rotate-90" : ""}`;
  let t20;
  if ($[35] !== t19) {
    t20 = <FontAwesomeIcon icon={faChevronRight} className={t19} />;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== handleAddReviewClick || $[38] !== t20) {
    t21 = <button className="group flex items-center py-2 mb-4 -ml-1" onClick={handleAddReviewClick}>{t17}{t18}{t20}</button>;
    $[37] = handleAddReviewClick;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  const t22 = `grid overflow-hidden transition-all duration-300 ease-in-out
                    ${addReviewFormOpen ? "grid-rows-[1fr] opacity-100 mt-3 mb-24" : "grid-rows-[0fr] opacity-0 mb-2 md:mb-8"}`;
  const t23 = `${process.env.NEXT_PUBLIC_RESOURCE_URL}/${sub_product_thumb}`;
  let t24;
  if ($[40] !== sub_product_name || $[41] !== t23) {
    t24 = <div className="hidden md:block overlay-block bg-white p-2.5"><Image className="" src={t23} alt={sub_product_name} placeholder="blur" blurDataURL="iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mPcIgkAAYcAz3yXgHoAAAAASUVORK5CYII=" width={150} height={150} /></div>;
    $[40] = sub_product_name;
    $[41] = t23;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="font-medium text-base text-primary me-1">Review:</div>;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== sub_product_name) {
    t26 = <div className="flex items-center mb-4 md:mb-6">{t25}<div className="font-medium text-base capitalize">{sub_product_name}</div></div>;
    $[44] = sub_product_name;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== reviewErrors.general) {
    t27 = <FormError addClasses="text-xs font-medium">{reviewErrors.general}</FormError>;
    $[46] = reviewErrors.general;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== reviewErrors.review_score) {
    t28 = <FormError>{reviewErrors.review_score}</FormError>;
    $[48] = reviewErrors.review_score;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="text-xs mb-3"><span className="text-xs me-1 font-medium text-light">Your Rating</span><span className="text-3xs text-light-3">(required)</span></div>;
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      maxWidth: 150
    };
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== newReviewScore) {
    t31 = <Rating value={newReviewScore} onChange={setNewReviewScore} style={t30} itemStyles={starStyles} />;
    $[52] = newReviewScore;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== newReviewScore) {
    t32 = newReviewScore > 0 && <>{newReviewScore} stars</>;
    $[54] = newReviewScore;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  let t33;
  if ($[56] !== t32) {
    t33 = <div className="text-base md:text-sm text-light italic ml-4 flex items-center">{t32}</div>;
    $[56] = t32;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  let t34;
  if ($[58] !== t31 || $[59] !== t33) {
    t34 = <div className="flex items-center">{t31}{t33}</div>;
    $[58] = t31;
    $[59] = t33;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== t28 || $[62] !== t34) {
    t35 = <div className="mb-6 md:mb-8">{t28}{t29}{t34}</div>;
    $[61] = t28;
    $[62] = t34;
    $[63] = t35;
  } else {
    t35 = $[63];
  }
  let t36;
  if ($[64] !== reviewErrors.user_alias) {
    t36 = <FormError>{reviewErrors.user_alias}</FormError>;
    $[64] = reviewErrors.user_alias;
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  const t37 = newReview.user_alias || "";
  let t38;
  if ($[66] !== handleInputChange || $[67] !== t37) {
    t38 = <input id="user_alias" type="text" name="user_alias" value={t37} onChange={handleInputChange} className="form-input peer" placeholder=" " required={true} />;
    $[66] = handleInputChange;
    $[67] = t37;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  let t39;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <label htmlFor="user_alias" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">User Alias <span className="text-primary">*</span></label>;
    $[69] = t39;
  } else {
    t39 = $[69];
  }
  let t40;
  if ($[70] !== t38) {
    t40 = <div className="form-group">{t38}{t39}</div>;
    $[70] = t38;
    $[71] = t40;
  } else {
    t40 = $[71];
  }
  let t41;
  if ($[72] !== t36 || $[73] !== t40) {
    t41 = <div className="w-full">{t36}{t40}</div>;
    $[72] = t36;
    $[73] = t40;
    $[74] = t41;
  } else {
    t41 = $[74];
  }
  let t42;
  if ($[75] !== reviewErrors.review_title) {
    t42 = <FormError>{reviewErrors.review_title}</FormError>;
    $[75] = reviewErrors.review_title;
    $[76] = t42;
  } else {
    t42 = $[76];
  }
  const t43 = newReview.review_title || "";
  let t44;
  if ($[77] !== handleInputChange || $[78] !== t43) {
    t44 = <input id="review_title" type="text" name="review_title" value={t43} onChange={handleInputChange} className="form-input peer" placeholder=" " required={true} />;
    $[77] = handleInputChange;
    $[78] = t43;
    $[79] = t44;
  } else {
    t44 = $[79];
  }
  let t45;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <label htmlFor="review_title" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Review Title <span className="text-primary">*</span></label>;
    $[80] = t45;
  } else {
    t45 = $[80];
  }
  let t46;
  if ($[81] !== t44) {
    t46 = <div className="form-group">{t44}{t45}</div>;
    $[81] = t44;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  let t47;
  if ($[83] !== t42 || $[84] !== t46) {
    t47 = <div className="w-full">{t42}{t46}</div>;
    $[83] = t42;
    $[84] = t46;
    $[85] = t47;
  } else {
    t47 = $[85];
  }
  let t48;
  if ($[86] !== reviewErrors.email) {
    t48 = <FormError>{reviewErrors.email}</FormError>;
    $[86] = reviewErrors.email;
    $[87] = t48;
  } else {
    t48 = $[87];
  }
  const t49 = newReview.email || "";
  let t50;
  if ($[88] !== handleInputChange || $[89] !== t49) {
    t50 = <input id="user_email" type="email" name="email" value={t49} onChange={handleInputChange} className="form-input peer" placeholder=" " required={true} />;
    $[88] = handleInputChange;
    $[89] = t49;
    $[90] = t50;
  } else {
    t50 = $[90];
  }
  let t51;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <label htmlFor="user_email" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Email <span className="text-primary">*</span></label>;
    $[91] = t51;
  } else {
    t51 = $[91];
  }
  let t52;
  if ($[92] !== t50) {
    t52 = <div className="form-group">{t50}{t51}</div>;
    $[92] = t50;
    $[93] = t52;
  } else {
    t52 = $[93];
  }
  let t53;
  if ($[94] !== t48 || $[95] !== t52) {
    t53 = <div className="w-full">{t48}{t52}</div>;
    $[94] = t48;
    $[95] = t52;
    $[96] = t53;
  } else {
    t53 = $[96];
  }
  let t54;
  if ($[97] !== t41 || $[98] !== t47 || $[99] !== t53) {
    t54 = <div className="flex flex-col md:flex-row items-center w-full justify-between gap-4 md:gap-6 mb-4 md:mb-6">{t41}{t47}{t53}</div>;
    $[97] = t41;
    $[98] = t47;
    $[99] = t53;
    $[100] = t54;
  } else {
    t54 = $[100];
  }
  let t55;
  if ($[101] !== reviewErrors.review) {
    t55 = <FormError>{reviewErrors.review}</FormError>;
    $[101] = reviewErrors.review;
    $[102] = t55;
  } else {
    t55 = $[102];
  }
  const t56 = newReview.review || "";
  let t57;
  if ($[103] !== handleInputChange || $[104] !== t56) {
    t57 = <textarea id="review" name="review" onChange={handleInputChange} value={t56} className="form-input peer min-h-[120px]" placeholder=" " required={true} />;
    $[103] = handleInputChange;
    $[104] = t56;
    $[105] = t57;
  } else {
    t57 = $[105];
  }
  let t58;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <label htmlFor="review" className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">Your Review <span className="text-primary">*</span></label>;
    $[106] = t58;
  } else {
    t58 = $[106];
  }
  let t59;
  if ($[107] !== t57) {
    t59 = <div className="form-group">{t57}{t58}</div>;
    $[107] = t57;
    $[108] = t59;
  } else {
    t59 = $[108];
  }
  let t60;
  if ($[109] !== t55 || $[110] !== t59) {
    t60 = <div className="mb-6 md:mb-8">{t55}{t59}</div>;
    $[109] = t55;
    $[110] = t59;
    $[111] = t60;
  } else {
    t60 = $[111];
  }
  const t61 = hasReviewErrors && "Please correct the errors above.";
  let t62;
  if ($[112] !== t61) {
    t62 = <FormError addClasses="text-xs font-medium mb-3">{t61}</FormError>;
    $[112] = t61;
    $[113] = t62;
  } else {
    t62 = $[113];
  }
  const t63 = `btn btn-sm btn-primary w-full sm:w-auto py-3 sm:py-2 ${isPending ? "text-black" : ""}`;
  let t64;
  if ($[114] !== isPending || $[115] !== t63) {
    t64 = <input type="submit" value="Add Review" disabled={isPending} className={t63} />;
    $[114] = isPending;
    $[115] = t63;
    $[116] = t64;
  } else {
    t64 = $[116];
  }
  const t65 = `text-sm sm:text-2xs py-3 sm:py-0 underline transition-colors duration-200 ${isPending ? "text-black" : "text-light hover:text-primary"}`;
  let t66;
  if ($[117] !== closeReviewAddForm || $[118] !== isPending || $[119] !== t65) {
    t66 = <button onClick={closeReviewAddForm} type="button" disabled={isPending} className={t65}>Cancel</button>;
    $[117] = closeReviewAddForm;
    $[118] = isPending;
    $[119] = t65;
    $[120] = t66;
  } else {
    t66 = $[120];
  }
  let t67;
  if ($[121] !== t64 || $[122] !== t66) {
    t67 = <div className="flex flex-col sm:flex-row justify-start gap-3 sm:gap-4 mb-4 w-full sm:w-auto">{t64}{t66}</div>;
    $[121] = t64;
    $[122] = t66;
    $[123] = t67;
  } else {
    t67 = $[123];
  }
  let t68;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = <div className="text-light text-3xs"><p className="text-3xs mb-2"><sup className="text-primary">*</sup> Your email will not be published. We might email you about this submission if we have questions or concerns about the content. Your review will be moderated by our staff and may take a few days to be published on the product page.</p><RecaptchaDisclaimer addClasses="text-3xs" /></div>;
    $[124] = t68;
  } else {
    t68 = $[124];
  }
  let t69;
  if ($[125] !== handleAddReviewSubmit || $[126] !== t27 || $[127] !== t35 || $[128] !== t54 || $[129] !== t60 || $[130] !== t62 || $[131] !== t67) {
    t69 = <form action={handleAddReviewSubmit}>{t27}{t35}{t54}{t60}{t62}{t67}{t68}</form>;
    $[125] = handleAddReviewSubmit;
    $[126] = t27;
    $[127] = t35;
    $[128] = t54;
    $[129] = t60;
    $[130] = t62;
    $[131] = t67;
    $[132] = t69;
  } else {
    t69 = $[132];
  }
  let t70;
  if ($[133] !== t26 || $[134] !== t69) {
    t70 = <div className="w-full">{t26}{t69}</div>;
    $[133] = t26;
    $[134] = t69;
    $[135] = t70;
  } else {
    t70 = $[135];
  }
  let t71;
  if ($[136] !== t24 || $[137] !== t70) {
    t71 = <div className="overflow-hidden"><div className="flex flex-col md:flex-row items-start gap-4 md:gap-6">{t24}{t70}</div></div>;
    $[136] = t24;
    $[137] = t70;
    $[138] = t71;
  } else {
    t71 = $[138];
  }
  let t72;
  if ($[139] !== t22 || $[140] !== t71) {
    t72 = <div className={t22}>{t71}</div>;
    $[139] = t22;
    $[140] = t71;
    $[141] = t72;
  } else {
    t72 = $[141];
  }
  let t73;
  if ($[142] !== handleVote || $[143] !== helpfulCounts || $[144] !== reviews || $[145] !== votingReviewId) {
    t73 = <div className="">{reviews && reviews.length > 0 ? <>{reviews.map((review_0, index) => <div key={index} className="texts-xs border-b-1 border-gray-5 pb-4 mb-4 lg:pb-5 lg:mb-5"><div className="grid grdi-cols-2 lg:grid-cols-4"><div className="lg:col-span-2 grid grid-cols-subgrid grid-cols-1 lg:grid-cols-2"><div className="mb-1"><div className="text-md font-extralight mb-2">{review_0.user_alias}</div><div className="text-2xs text-light"><ReviewTime timestamp={review_0.added_ts} /></div></div><div className="mb-6"><div className="flex items-center"><Rating value={review_0.review_score} readOnly={true} style={{
                    maxWidth: 80
                  }} itemStyles={starStyles} /><div className="ml-1 text-xs text-light"><span className="font-semibold">{review_0.review_score}</span>/5</div></div></div></div><div className="lg:col-span-2"><div className="text-xs font-medium capitalize mb-3">{review_0.review_title}</div><p className="text-xs">{review_0.review}</p><div className="flex items-center space-x-3 mt-4"><button type="button" onClick={() => handleVote(review_0.review_id, "helpful")} disabled={votingReviewId === review_0.review_id} className={`text-2xs pr-3 border-r-1 border-gray-20 hover:text-primary transition-colors ${votingReviewId === review_0.review_id ? "opacity-50" : ""}`}>{(helpfulCounts[review_0.review_id]?.helpful_count || 0) > 0 && <span className="mr-1">({helpfulCounts[review_0.review_id]?.helpful_count})</span>}helpful<FontAwesomeIcon icon={faThumbsUp} className="text-2xs ml-1 text-light" /></button><button type="button" onClick={() => handleVote(review_0.review_id, "not_helpful")} disabled={votingReviewId === review_0.review_id} className={`text-2xs hover:text-primary transition-colors ${votingReviewId === review_0.review_id ? "opacity-50" : ""}`}>{(helpfulCounts[review_0.review_id]?.not_helpful_count || 0) > 0 && <span className="mr-1">({helpfulCounts[review_0.review_id]?.not_helpful_count})</span>}not helpful<FontAwesomeIcon icon={faThumbsDown} className="text-2xs ml-1 text-light" /></button></div></div></div></div>)}</> : <div className="text-center pb-12"><div className="flex justify-center mb-4"><Rating value={0} readOnly={true} style={{
            maxWidth: 100
          }} itemStyles={starStyles} /></div><div className="text-sm font-medium">There are no reviews of this product yet.</div></div>}</div>;
    $[142] = handleVote;
    $[143] = helpfulCounts;
    $[144] = reviews;
    $[145] = votingReviewId;
    $[146] = t73;
  } else {
    t73 = $[146];
  }
  let t74;
  if ($[147] !== t10 || $[148] !== t16 || $[149] !== t21 || $[150] !== t72 || $[151] !== t73) {
    t74 = <div className={t10}>{t16}{t21}{t72}{t73}</div>;
    $[147] = t10;
    $[148] = t16;
    $[149] = t21;
    $[150] = t72;
    $[151] = t73;
    $[152] = t74;
  } else {
    t74 = $[152];
  }
  return t74;
};
export default Reviews;